/**
 * docs/BILLING.md — packages and USDT payments over the API: the signed-in user's side and the
 * administrator's. Every mutation invalidates the whole `billing` key space (a payment changes
 * the user's package, their usage and the admin's lists at once).
 */

import { useEffect } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { cleanParams, http, type ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import type { Paginated, UUID } from '@/types/api';

import type {
  AdminInvoice,
  AdminPlan,
  BillingMe,
  BillingSettings,
  BillingSettingsWrite,
  BillingSummary,
  BinanceTest,
  Campaign,
  CampaignWrite,
  Deposit,
  FeatureKey,
  FeatureOrder,
  Invoice,
  InvoiceStatus,
  PaymentAddress,
  PaymentAddressWrite,
  PlansResponse,
  PlanWrite,
  Quote,
  SignupDimension,
  SignupSources,
  UserBilling,
} from './types';
import { hasFeature } from './types';

export const billingKeys = {
  all: ['billing'] as const,
  me: ['billing', 'me'] as const,
  plans: ['billing', 'plans'] as const,
  invoices: ['billing', 'invoices'] as const,
  invoice: (id: UUID) => ['billing', 'invoice', id] as const,
  admin: {
    all: ['billing', 'admin'] as const,
    plans: ['billing', 'admin', 'plans'] as const,
    campaigns: ['billing', 'admin', 'campaigns'] as const,
    addresses: ['billing', 'admin', 'addresses'] as const,
    settings: ['billing', 'admin', 'settings'] as const,
    featureOrder: ['billing', 'admin', 'feature-order'] as const,
    summary: ['billing', 'admin', 'summary'] as const,
    signups: (by: SignupDimension, days: number) =>
      ['billing', 'admin', 'signups', by, days] as const,
    invoices: (filters: AdminInvoiceFilters) =>
      ['billing', 'admin', 'invoices', cleanParams({ ...filters })] as const,
    deposits: (filters: DepositFilters) =>
      ['billing', 'admin', 'deposits', cleanParams({ ...filters })] as const,
    user: (id: UUID) => ['billing', 'admin', 'user', id] as const,
  },
};

/** An open invoice is polled while its page is open, so "paid" shows without a reload. */
export const INVOICE_POLL_MS = 10_000;
/** What the user may use changes rarely: on a payment, or when an administrator acts. */
const ME_STALE_MS = 60_000;

/* -------------------------------------------------------------------------- */
/* The signed-in user                                                          */
/* -------------------------------------------------------------------------- */

export function useBillingMe(): UseQueryResult<BillingMe, ApiError> {
  const signedIn = useAuthStore((state) => state.isAuthenticated);
  return useQuery<BillingMe, ApiError>({
    queryKey: billingKeys.me,
    queryFn: ({ signal }) => http.get<BillingMe>('/billing/me', { signal }),
    staleTime: ME_STALE_MS,
    enabled: signedIn,
  });
}

/**
 * Whether the user's package opens `feature`. `allowed` stays true while the answer loads, so a
 * page never flashes its lock before it knows — the API refuses anyway if it should.
 */
export function useFeature(feature: FeatureKey): {
  allowed: boolean;
  loading: boolean;
  me: BillingMe | undefined;
} {
  const query = useBillingMe();
  const me = query.data;
  const allowed =
    me === undefined ? true : me.exempt || !me.enforced || hasFeature(me.features, feature);
  return { allowed, loading: query.isLoading, me };
}

/**
 * The strict form of `useFeature`, for a switch that moves real orders (`atr_live`): only an
 * administrator or a package that names it opens it — billing being switched off does not, and
 * nothing opens it while the answer loads.
 */
export function useIncludes(feature: FeatureKey): boolean {
  const me = useBillingMe().data;
  return me !== undefined && (me.exempt || hasFeature(me.features, feature));
}

/**
 * Whether a "Future" row (`NavItem.future`) opens its page: only once the package is known to
 * include it — until the answer comes, and without it, the row stays a greyed roadmap entry.
 */
export function futureOpen(me: BillingMe | undefined, feature: FeatureKey | undefined): boolean {
  return (
    feature !== undefined &&
    me !== undefined &&
    (me.exempt || !me.enforced || hasFeature(me.features, feature))
  );
}

export function useFutureOpen(feature: FeatureKey | undefined): boolean {
  return futureOpen(useBillingMe().data, feature);
}

/** A navigation row whose page the package does not open shows a lock. */
export function useNavLock(feature: FeatureKey | undefined): boolean {
  const { allowed } = useFeature(feature ?? 'grid');
  return feature !== undefined && !allowed;
}

export function usePlans(): UseQueryResult<PlansResponse, ApiError> {
  return useQuery<PlansResponse, ApiError>({
    queryKey: billingKeys.plans,
    queryFn: ({ signal }) => http.get<PlansResponse>('/billing/plans', { signal }),
  });
}

export function useQuote(): UseMutationResult<
  Quote,
  ApiError,
  { plan_id: UUID; months: number; code?: string | null }
> {
  return useMutation<Quote, ApiError, { plan_id: UUID; months: number; code?: string | null }>({
    mutationFn: (body) => http.post<Quote>('/billing/quote', body),
  });
}

export function useInvoices(): UseQueryResult<Invoice[], ApiError> {
  return useQuery<Invoice[], ApiError>({
    queryKey: billingKeys.invoices,
    queryFn: ({ signal }) => http.get<Invoice[]>('/billing/invoices', { signal }),
  });
}

const OPEN: InvoiceStatus[] = ['pending', 'expired'];

export function useInvoice(id: UUID | undefined): UseQueryResult<Invoice, ApiError> {
  const queryClient = useQueryClient();
  const query = useQuery<Invoice, ApiError>({
    queryKey: billingKeys.invoice(id ?? ''),
    queryFn: ({ signal }) => http.get<Invoice>(`/billing/invoices/${id}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data && OPEN.includes(query.state.data.status) ? INVOICE_POLL_MS : false,
  });
  // A watcher can confirm payment without a browser mutation. Refresh entitlements as
  // soon as polling sees paid, so the workspace can offer the newly included system.
  useEffect(() => {
    if (query.data?.status === 'paid') {
      void queryClient.invalidateQueries({ queryKey: billingKeys.me });
    }
  }, [query.data?.id, query.data?.status, queryClient]);
  return query;
}

function useBillingMutation<TData, TBody>(
  mutationFn: (body: TBody) => Promise<TData>,
): UseMutationResult<TData, ApiError, TBody> {
  const queryClient = useQueryClient();
  return useMutation<TData, ApiError, TBody>({
    mutationFn,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

export function useCreateInvoice(): UseMutationResult<
  Invoice,
  ApiError,
  { plan_id: UUID; months: number; payment_address_id: UUID; code?: string | null }
> {
  return useBillingMutation((body) => http.post<Invoice>('/billing/invoices', body));
}

export function useSubmitTxId(): UseMutationResult<Invoice, ApiError, { id: UUID; tx_id: string }> {
  return useBillingMutation(({ id, tx_id }) =>
    http.post<Invoice>(`/billing/invoices/${id}/txid`, { tx_id }),
  );
}

export function useCheckInvoice(): UseMutationResult<Invoice, ApiError, UUID> {
  return useBillingMutation((id) => http.post<Invoice>(`/billing/invoices/${id}/check`));
}

export function useCancelInvoice(): UseMutationResult<Invoice, ApiError, UUID> {
  return useBillingMutation((id) => http.post<Invoice>(`/billing/invoices/${id}/cancel`));
}

/* -------------------------------------------------------------------------- */
/* Administrator                                                               */
/* -------------------------------------------------------------------------- */

export interface Reason {
  reason: string;
}

export interface AdminInvoiceFilters {
  status?: InvoiceStatus | '';
  user_id?: UUID;
  page?: number;
  page_size?: number;
}

export interface DepositFilters {
  state?: 'unmatched' | 'matched' | 'ignored' | 'all';
  page?: number;
  page_size?: number;
}

export function useAdminPlans(): UseQueryResult<AdminPlan[], ApiError> {
  return useQuery<AdminPlan[], ApiError>({
    queryKey: billingKeys.admin.plans,
    queryFn: ({ signal }) => http.get<AdminPlan[]>('/admin/billing/plans', { signal }),
  });
}

export function useAdminCampaigns(): UseQueryResult<Campaign[], ApiError> {
  return useQuery<Campaign[], ApiError>({
    queryKey: billingKeys.admin.campaigns,
    queryFn: ({ signal }) => http.get<Campaign[]>('/admin/billing/campaigns', { signal }),
  });
}

export function useAdminAddresses(): UseQueryResult<PaymentAddress[], ApiError> {
  return useQuery<PaymentAddress[], ApiError>({
    queryKey: billingKeys.admin.addresses,
    queryFn: ({ signal }) => http.get<PaymentAddress[]>('/admin/billing/addresses', { signal }),
  });
}

export function useFeatureOrder(): UseQueryResult<FeatureOrder, ApiError> {
  return useQuery<FeatureOrder, ApiError>({
    queryKey: billingKeys.admin.featureOrder,
    queryFn: ({ signal }) => http.get<FeatureOrder>('/admin/billing/feature-order', { signal }),
  });
}

export function useSaveFeatureOrder(): UseMutationResult<
  FeatureOrder,
  ApiError,
  { order: FeatureKey[]; reason?: string }
> {
  return useBillingMutation((body) => http.put<FeatureOrder>('/admin/billing/feature-order', body));
}

export function useBillingSettings(): UseQueryResult<BillingSettings, ApiError> {
  return useQuery<BillingSettings, ApiError>({
    queryKey: billingKeys.admin.settings,
    queryFn: ({ signal }) => http.get<BillingSettings>('/admin/billing/settings', { signal }),
    refetchInterval: 30_000,
  });
}

export function useBillingSummary(): UseQueryResult<BillingSummary, ApiError> {
  return useQuery<BillingSummary, ApiError>({
    queryKey: billingKeys.admin.summary,
    queryFn: ({ signal }) => http.get<BillingSummary>('/admin/billing/summary', { signal }),
    refetchInterval: 30_000,
  });
}

/** docs/BILLING.md §9.2 — sign-ups per source of the last `days` (0 = all), and their funnel. */
export function useSignupSources(
  by: SignupDimension,
  days: number,
): UseQueryResult<SignupSources, ApiError> {
  return useQuery<SignupSources, ApiError>({
    queryKey: billingKeys.admin.signups(by, days),
    queryFn: ({ signal }) =>
      http.get<SignupSources>('/admin/billing/signups', { params: { by, days }, signal }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useAdminInvoices(
  filters: AdminInvoiceFilters,
): UseQueryResult<Paginated<AdminInvoice>, ApiError> {
  return useQuery<Paginated<AdminInvoice>, ApiError>({
    queryKey: billingKeys.admin.invoices(filters),
    queryFn: ({ signal }) =>
      http.get<Paginated<AdminInvoice>>('/admin/billing/invoices', {
        params: cleanParams({ ...filters }),
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

export function useAdminDeposits(
  filters: DepositFilters,
): UseQueryResult<Paginated<Deposit>, ApiError> {
  return useQuery<Paginated<Deposit>, ApiError>({
    queryKey: billingKeys.admin.deposits(filters),
    queryFn: ({ signal }) =>
      http.get<Paginated<Deposit>>('/admin/billing/deposits', {
        params: cleanParams({ ...filters }),
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

export function useUserBilling(id: UUID | undefined): UseQueryResult<UserBilling, ApiError> {
  return useQuery<UserBilling, ApiError>({
    queryKey: billingKeys.admin.user(id ?? ''),
    queryFn: ({ signal }) => http.get<UserBilling>(`/admin/billing/users/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useSavePlan(): UseMutationResult<AdminPlan, ApiError, PlanWrite & { id?: UUID }> {
  return useBillingMutation(({ id, ...body }) =>
    id
      ? http.put<AdminPlan>(`/admin/billing/plans/${id}`, body)
      : http.post<AdminPlan>('/admin/billing/plans', body),
  );
}

export function useDeletePlan(): UseMutationResult<void, ApiError, UUID> {
  return useBillingMutation((id) => http.delete<void>(`/admin/billing/plans/${id}`));
}

export function useSaveCampaign(): UseMutationResult<
  Campaign,
  ApiError,
  CampaignWrite & { id?: UUID }
> {
  return useBillingMutation(({ id, ...body }) =>
    id
      ? http.put<Campaign>(`/admin/billing/campaigns/${id}`, body)
      : http.post<Campaign>('/admin/billing/campaigns', body),
  );
}

export function useDeleteCampaign(): UseMutationResult<void, ApiError, UUID> {
  return useBillingMutation((id) => http.delete<void>(`/admin/billing/campaigns/${id}`));
}

export function useSaveAddress(): UseMutationResult<
  PaymentAddress,
  ApiError,
  PaymentAddressWrite & { id?: UUID }
> {
  return useBillingMutation(({ id, ...body }) =>
    id
      ? http.put<PaymentAddress>(`/admin/billing/addresses/${id}`, body)
      : http.post<PaymentAddress>('/admin/billing/addresses', body),
  );
}

export function useDeleteAddress(): UseMutationResult<void, ApiError, UUID> {
  return useBillingMutation((id) => http.delete<void>(`/admin/billing/addresses/${id}`));
}

export function useSaveBillingSettings(): UseMutationResult<
  BillingSettings,
  ApiError,
  BillingSettingsWrite
> {
  return useBillingMutation((body) => http.put<BillingSettings>('/admin/billing/settings', body));
}

export function useSetBinanceKey(): UseMutationResult<
  BillingSettings,
  ApiError,
  { api_key: string; api_secret: string }
> {
  return useBillingMutation((body) => http.put<BillingSettings>('/admin/billing/binance', body));
}

export function useClearBinanceKey(): UseMutationResult<BillingSettings, ApiError, void> {
  return useBillingMutation(() => http.delete<BillingSettings>('/admin/billing/binance'));
}

export function useTestBinanceKey(): UseMutationResult<BinanceTest, ApiError, void> {
  return useMutation<BinanceTest, ApiError, void>({
    mutationFn: () => http.post<BinanceTest>('/admin/billing/binance/test'),
  });
}

export function useCheckDepositsNow(): UseMutationResult<{ status: string }, ApiError, void> {
  return useBillingMutation(() => http.post<{ status: string }>('/admin/billing/check'));
}

export function useMarkInvoicePaid(): UseMutationResult<
  AdminInvoice,
  ApiError,
  Reason & { id: UUID; amount?: string | null }
> {
  return useBillingMutation(({ id, ...body }) =>
    http.post<AdminInvoice>(`/admin/billing/invoices/${id}/mark-paid`, body),
  );
}

export function useAdminCancelInvoice(): UseMutationResult<
  AdminInvoice,
  ApiError,
  Reason & { id: UUID }
> {
  return useBillingMutation(({ id, ...body }) =>
    http.post<AdminInvoice>(`/admin/billing/invoices/${id}/cancel`, body),
  );
}

export function useAssignDeposit(): UseMutationResult<
  AdminInvoice,
  ApiError,
  Reason & { id: UUID; invoice_id: UUID }
> {
  return useBillingMutation(({ id, ...body }) =>
    http.post<AdminInvoice>(`/admin/billing/deposits/${id}/assign`, body),
  );
}

export function useIgnoreDeposit(): UseMutationResult<
  Deposit,
  ApiError,
  Reason & { id: UUID; ignored: boolean }
> {
  return useBillingMutation(({ id, ...body }) =>
    http.post<Deposit>(`/admin/billing/deposits/${id}/ignore`, body),
  );
}

export function useAssignSubscription(): UseMutationResult<
  UserBilling,
  ApiError,
  Reason & { userId: UUID; plan_id: UUID; ends_at: string | null; status?: 'active' | 'trial' }
> {
  return useBillingMutation(({ userId, ...body }) =>
    http.put<UserBilling>(`/admin/billing/users/${userId}`, body),
  );
}

export function useRemoveSubscription(): UseMutationResult<
  UserBilling,
  ApiError,
  Reason & { userId: UUID }
> {
  return useBillingMutation(({ userId, ...body }) =>
    http.post<UserBilling>(`/admin/billing/users/${userId}/remove`, body),
  );
}
